import React, { useState, useEffect, useCallback } from "react";
import { Tabs, Tab, styled, CircularProgress } from "@mui/material";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import ViewIcon from "@styles/all-images/icons/view-icon.svg";
import ChatIcon from "@styles/all-images/icons/ChatIconBlue.svg";
import CheckBlueIcon from "@styles/all-images/icons/checkblue.svg";
import CloseBlueIcon from "@styles/all-images/icons/closeblue.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import CustomTable from "@/components/common/table/CustomTable";
import { toast } from "react-toastify";
import RejectionReason from "@/pages/TierConfiguration/TierConfigurationRejectionReason";
import { EndpointType } from "@/constants/api";
import dateHandler from "@/composables/dateUtil";
import { IMTSDocumentApprovalStatus } from "@/constants/enums/im-ts-document-status.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import {
  DocumentsPermissions,
  SignPermissions,
} from "@/constants/permission-modules/base-permissions";
import StandaloneChatDrawer, {
  StandaloneChatDrawerType,
} from "../LiveChat/StandaloneChatDrawer";
import { CreateGroupResponse } from "../LiveChat/types/api-response-types";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
// import ChatComponent from "./ChatComponent";

// Styled components for tabs
const StyledTabs = styled(Tabs)({
  minHeight: "unset",
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

const StyledTab = styled(Tab)({
  minHeight: "unset",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  minWidth: "unset",
});

type DocumentType = "toBeSignedDocuments" | "signedDocuments";

const tabs = [
  {
    label: "Documents to be Signed",
    type: "toBeSignedDocuments" as DocumentType,
    endpoint: "documentToBeSigned",
  },
  {
    label: "Signed Documents",
    type: "signedDocuments" as DocumentType,
    endpoint: "signedDocuments",
  },
];

interface IndividualDocumentSignedItem {
  client_entity: string;
  document_name: string;
  document_type: string;
  id: number;
  sent_by: string;
  sent_by_id: number;
  submission_date: string;
  room_id?: number;
}

const SignedDocuments = () => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") as DocumentType;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [activeTab, setActiveTab] = useState<number>(
    tab === "signedDocuments" ? 1 : 0,
  );
  const [loading, setLoading] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const [individualChatUserId, setIndividualChatUserId] = useState<
    number | null
  >(null);
  const [individualChatRoomId, setIndividualChatRoomId] = useState<
    string | null
  >(null);
  const [isIndividualChatOpen, setIsIndividualChatOpen] = useState(false);
  const [isIndividualChatCreationLoading, setIsIndividualChatCreationLoading] =
    useState<{ [key: number]: boolean }>({});

  const { formatDate } = dateHandler();
  const { isPermitted } = usePermissionState();
  // Action handlers
  const handleView = (row: any) => {
    navigate(
      `/view-signed-document/${row.id}?type=${row.document_type.split(" ")[0].toLowerCase()}&history=false`,
    );
  };

  const handleTabChange = (_: any, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    const previousTab =
      activeTab === 0 ? "toBeSignedDocuments" : "signedDocuments";
    navigate(`/menu?lastTab=${previousTab}`);
  };

  useEffect(() => {
    navigate(
      `${window.location.pathname}?tab=${activeTab === 0 ? "toBeSignedDocuments" : "signedDocuments"}`,
      { replace: true },
    );
    setForceRefresh(true);
  }, [activeTab]);

  const handleReject = (row: any) => {
    setSelectedItem(row);
    setIsRejectionModalOpen(true);
  };

  const handleRejectSubmit = async (reason?: string) => {
    try {
      setLoading(true);
      const endpoint: EndpointType = "approveRejectIMTSDocument";
      const { authenticatedCall } = actionsHandler(endpoint);
      const response = await dispatch(
        authenticatedCall({
          method: "put",
          payload: {
            status: IMTSDocumentApprovalStatus.Rejected,
            note: reason,
          },
          customParams: [`${selectedItem.id}/`],
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message || "Document rejected successfully",
        );
        setIsRejectionModalOpen(false);
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  // Get table columns based on document type
  const getColumns = (type: DocumentType) => {
    const baseColumns = [
      {
        header: "Document Name",
        accessor: "document_name",
        sortable: true,
        sortKey: "document_title",
      },
      {
        header: "Client/Entity",
        accessor: "client_entity",
        sortable: true,
        sortKey: "client_entity",
      },
      {
        header: "Sent by",
        accessor: "sent_by",
        sortable: true,
        sortKey: "sent_by",
      },
      {
        header: "Document Type",
        accessor: "document_type",
        sortable: true,
        sortKey: "document_type",
      },
      {
        header: `${type === "toBeSignedDocuments" ? "Submission Date" : "Signed Date"}`,
        accessor: (row: any) =>
          `${type === "toBeSignedDocuments" ? formatDate(row.submission_date) : formatDate(row.signed_date)}`,
        sortable: true,
        sortKey: `${type === "toBeSignedDocuments" ? "submission_date" : "signed_date"}`,
      },
      {
        header: "Actions",
        accessor: (row: any) => (
          <div className="tw-flex tw-items-center tw-justify-left">
            <button onClick={() => handleView(row)} title="View">
              <img
                src={ViewIcon}
                alt="View"
                className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
              />
            </button>
            <button onClick={() => handleIndividualChat(row)} title="Chat">
              {isIndividualChatCreationLoading[row.id] ? (
                <CustomLoader loading={true} isWrapper={true} size="20" />
              ) : (
                <img
                  src={ChatIcon}
                  alt="Chat"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              )}
            </button>
            {type === "toBeSignedDocuments" &&
              isPermitted(SignPermissions.APPROVE_REJECT_SIGN) && (
                <div className="tw-flex tw-items-center tw-justify-left">
                  <button onClick={() => handleView(row)} title="Approve">
                    <img
                      src={CheckBlueIcon}
                      alt="Approve"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                  <button onClick={() => handleReject(row)} title="Reject">
                    <img
                      src={CloseBlueIcon}
                      alt="Reject"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  </button>
                </div>
              )}
          </div>
        ),
      },
    ];

    return baseColumns;
  };

  // Fetch data for the table
  const fetchDocuments = useCallback(
    async (params: {
      page?: number;
      limit?: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const currentTab = tabs[activeTab];
        const { getAllData } = actionsHandler(
          currentTab.endpoint as EndpointType,
        );

        let ordering = "";
        if (params.sortColumn) {
          ordering =
            params.sortDirection === "desc"
              ? `-${params.sortColumn}`
              : params.sortColumn;
        }

        const response = await dispatch(
          getAllData({
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
            customQuery: ordering ? `ordering=${ordering}` : "",
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching documents:", error);
        toast.error("Failed to fetch documents");
        return {};
      }
    },
    [activeTab, dispatch],
  );

  const handleCreatingIndividualChatRoom = (
    userId: number,
    id: number,
  ): Promise<CreateGroupResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [id]: true,
        }));
        const { authenticatedCall } = actionsHandler("createIndividualChat");
        const response = await dispatch(
          authenticatedCall({
            method: "post",
            payload: {
              recipient_id: userId,
            },
          }),
        );

        if (response.ok) {
          resolve(response.data as CreateGroupResponse);
        }
      } catch (error) {
        console.error("Error creating individual chat:", error);
        reject(error);
      } finally {
        setIsIndividualChatCreationLoading((prev) => ({
          ...prev,
          [id]: false,
        }));
      }
    });
  };

  const handleIndividualChat = async (item: IndividualDocumentSignedItem) => {
    if (item.room_id) {
      setIndividualChatRoomId(item.room_id?.toString() || null);
    } else {
      const data = await handleCreatingIndividualChatRoom(
        item.sent_by_id,
        item.id,
      );
      setIndividualChatRoomId(data.room_id.toString() || null);
    }
    setIndividualChatUserId(item.sent_by_id);
    setIsIndividualChatOpen(true);
  };

  useEffect(() => {
    if (!isIndividualChatOpen) {
      setIndividualChatRoomId(null);
      setIndividualChatUserId(null);
    }
  }, [isIndividualChatOpen]);

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-6 tw-pt-4 tw-bg-white tw-border tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                onClick={handleBack}
                className="tw-cursor-pointer"
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Sign
              </h1>
            </div>
          </div>

          <div className="tw-w-fit tw-mt-4 tw-border tw-border-[#E9EAEB]">
            <StyledTabs value={activeTab} onChange={handleTabChange}>
              {tabs.map((tab, index) => (
                <StyledTab
                  key={index}
                  label={tab.label}
                  className={`tw-text-sm tw-font-semibold ${
                    activeTab === index ? "active-tab" : "tw-text-[#232323]"
                  } tab-border`}
                />
              ))}
            </StyledTabs>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-mt-[177px]">
        <CustomTable
          columns={getColumns(tabs[activeTab].type)}
          fetchData={fetchDocuments}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          forceReset={forceReset}
          setForceReset={setForceReset}
          noDataMessage={`No ${activeTab === 0 ? "documents available for signing at the moment." : "signed documents available at the moment."}`}
          noSearchDataMessage={`No matching ${activeTab === 0 ? "documents to be signed" : "signed documents"} found`}
        />
      </div>

      {/* Modals */}
      {isRejectionModalOpen && (
        <RejectionReason
          onClose={() => setIsRejectionModalOpen(false)}
          onSubmit={handleRejectSubmit}
          isLoading={loading}
        />
      )}

      {individualChatUserId && individualChatRoomId && (
        <StandaloneChatDrawer
          isChatNotificationOpen={isIndividualChatOpen}
          setIsChatNotificationOpen={setIsIndividualChatOpen}
          userId={individualChatUserId}
          customChatRoomId={individualChatRoomId}
          type={StandaloneChatDrawerType.USER}
        />
      )}
    </div>
  );
};

export default SignedDocuments;
